import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React from 'react';
import { Link } from 'react-router-dom';
import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName, getScenarioStatusName } from 'src/api/types';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { formatDate, getPercentage, numeralFormat } from 'src/utils/misc';
import GenericTable, { GTColumnSelectAnchor, GTExpander, GTModifierLabel, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { getIncompleteStateNew, IncompleteStateMarkerNew, TableSkeleton, TextOverflowTooltip } from 'src/common';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { TLLink } from 'src/pages/DashboardPage/comps';
import {
  ControlRmpQuery,
  useControlRmpQuery,
  useUnlinkScenarioFromControlsMutation,
} from 'src/graphql/_generated';
import { checkIsModifierStrategy } from 'src/components/Modifier/util';
import { Modifier } from 'src/components/Modifier';
import {
  computeCumulativeStartingValues,
  convertToModifiers,
  convertToModifiersGql,
} from 'src/components/ProjectedRisk/util';
import { useCache } from 'src/graphql';
import useVersion from 'src/hooks/useVersion';

interface ControlTableExpandContentProps {
  id: string;
}

const RmpScenarioModifierExpand: React.FC<{
  controlData: NonNullable<ControlRmpQuery['control']>;
  scenarioId: string;
}> = ({ controlData, scenarioId }) => {
  const { refetchQuery } = useCache();
  const { versionId } = useVersion();

  const rmp = controlData.riskManagementPlans.find((r) => r.scenario?.id === scenarioId);

  if (!rmp || !rmp.scenario) return null;

  const recommendedModifiers = rmp.recommendedModifiers ? convertToModifiers(rmp.recommendedModifiers) : null;

  const baseFrequencyTimes = getPercentage(
    rmp.scenario.baseRisk?.frequencyTimes,
    rmp.scenario.baseRisk?.frequencyYears,
  );

  const rmpIndex = controlData.riskManagementPlans.findIndex((r) => r.scenario?.id === scenarioId);
  const cumulativeStarting = computeCumulativeStartingValues(controlData.riskManagementPlans, rmpIndex, {
    frequencyTimes: baseFrequencyTimes,
    lower: rmp.scenario.baseRisk?.lower || 0,
    upper: rmp.scenario.baseRisk?.upper || 0,
  });

  return (
    <Modifier
      control={{
        id: controlData.id,
        strategy: controlData.strategy,
        status: controlData.status,
        modifiers: convertToModifiersGql(rmp.modifiers || []),
      }}
      projectedScenario={{
        id: scenarioId,
        upper: rmp.scenario.baseRisk?.upper || 0,
        lower: rmp.scenario.baseRisk?.lower || 0,
        frequency_years: rmp.scenario.baseRisk?.frequencyYears || 0,
        frequency_times: baseFrequencyTimes,
      }}
      startingValues={cumulativeStarting}
      disabled={!!versionId}
      onSave={async () => {
        await refetchQuery({ include: ['controlRmp'] });
      }}
      withCopy
      recommendedModifiers={recommendedModifiers}
      bgColor="aliceBlue"
    />
  );
};

const defaultColumns = [
  'status_scen',
  'name',
  'source',
  'event',
  'consequence',
  'expected_loss',
  'updated_at',
  'edited_at',
  '_mod_applied',
];
const internalIds = [GTInternalIds.expander, GTInternalIds.tdm];

export const ControlTableExpandContent: React.FC<ControlTableExpandContentProps> = ({ id }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.rmpScenarios}-columns`, defaultColumns);
  const { refetchQuery } = useCache();
  const { versionId } = useVersion();

  const { data: controlRmpData } = useControlRmpQuery({ variables: { id, versionId } });

  const controlData = controlRmpData?.control;
  const isModStrategy = checkIsModifierStrategy(controlData?.strategy);

  const data = controlData?.riskManagementPlans.map((el) => ({ ...el, scenario: el.scenario! })).filter(Boolean) || [];

  const resetCache = async () => refetchQuery({ include: ['controls', 'controlRmp'] });

  const [unlinkScenario] = useUnlinkScenarioFromControlsMutation({
    onCompleted: resetCache,
  });

  type Scenario = (typeof data)[number];

  const columns: ColumnDef<Scenario>[] = [
    {
      id: GTInternalIds.expander,
      header: () => null,
      cell: ({ row }) => {
        return row.getCanExpand() ? (
          <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
        ) : null;
      },
      size: 1,
      maxSize: 20,
      enableSorting: false,
    },
    {
      id: 'assessment_name',
      header: 'Assessment',
      accessorFn: (row) => row.scenario.assessment.name,
      cell: (info) => info.getValue(),
      sortUndefined: 'last',
      sortingFn: sortingFns.text,
    },
    {
      id: 'ux_id',
      header: 'Id',
      accessorFn: (row) => row.scenario.id,
      cell: (info) => info.getValue(),
    },
    {
      id: 'status_scen',
      header: 'Status',
      accessorFn: (row) => getScenarioStatusName(row.scenario.status),
      cell: (info) => info.getValue(),
    },
    {
      id: 'name',
      header: 'Scenario',
      accessorFn: (row) => row.scenario.name,
      cell: (info) => (
        <TextOverflowTooltip>
          <Link
            className="table-link"
            to={`/risk-assessments/${info.row.original.scenario.assessment.id}/scenario/${info.row.original.scenario.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            {info.getValue() as string}
          </Link>
        </TextOverflowTooltip>
      ),
      sortUndefined: 'last',
      sortingFn: sortingFns.text,
    },
    {
      id: 'source',
      header: 'Source',
      accessorFn: (row) => getRiskSourcesName(row.scenario.source),
      cell: (info) => info.getValue(),
    },
    {
      id: 'event',
      header: 'Event',
      accessorFn: (row) => getRiskEventsName(row.scenario.event),
      cell: (info) => info.getValue(),
    },
    {
      id: 'consequence',
      header: 'Consequence',
      accessorFn: (row) => getRiskConsequencesName(row.scenario.consequence),
      cell: (info) => info.getValue(),
    },
    {
      id: 'risk_owner',
      header: 'Risk Owner',
      accessorFn: (row) => row.scenario.ownerName,
      cell: (info) => info.getValue(),
      sortingFn: sortingFns.text,
    },
    {
      id: 'expected_loss',
      header: 'Expected Loss',
      accessorFn: (row) => row.scenario.liveRisk?.expectedLoss,
      cell: (info) => numeralFormat(info.getValue() as number),
    },
    {
      id: 'risk_identified_date',
      header: 'Identified Date',
      accessorFn: (row) => row.scenario.riskIdentifiedDate || '',
      cell: (info) => <TLLink date={info.getValue() as string}>{formatDate(info.getValue() as string)}</TLLink>,
    },
    {
      id: 'updated_at',
      header: 'Last Updated',
      accessorFn: (row) => row.scenario.meta?.updatedAt || null,
      cell: ({ row }) => {
        const date = row.original.scenario.meta?.updatedAt;
        if (!date) return null;
        return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
      },
    },
    {
      id: 'edited_at',
      header: 'Last Edited Date',
      accessorFn: (row) => row.scenario.editedAt || null,
      cell: ({ row }) => (
        <TLLink date={row.original.scenario.editedAt}>
          {formatDate(row.original.scenario.editedAt, { formatType: 'datetime' })}
        </TLLink>
      ),
    },
    {
      id: 'created_at',
      header: 'Created Date',
      accessorFn: (row) => row.scenario.meta?.createdAt || null,
      cell: ({ row }) => {
        const date = row.original.scenario.meta?.createdAt;
        if (!date) return null;
        return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
      },
    },
    {
      id: '_mod_applied',
      header: 'Modifiers',
      accessorFn: (row) => row.modifiers?.length || 0,
      cell: (info) => {
        if (!isModStrategy) return null;
        return (
          <GTModifierLabel
            length={info.getValue() as number}
            satisfiesModifier={getIncompleteStateNew(info.row.original.scenario) !== 'TECH'}
            plainText
          />
        );
      },
    },
    {
      id: GTInternalIds.tdm,
      header: () => null,
      cell: ({ row }) => {
        const handleUnlinkScenario = () => {
          unlinkScenario({
            variables: {
              scenarioId: row.original.scenario.id,
              controlIds: [id],
            },
          });
        };

        return (
          <GTTdm
            extraComponentLeft={<IncompleteStateMarkerNew scenario={row.original.scenario} />}
            onUnlinkScenarioClick={handleUnlinkScenario}
          />
        );
      },
      size: 60,
    },
  ];

  if (!controlRmpData) return <TableSkeleton />;

  return (
    <GenericTable
      tableId={TableIds.rmpScenarios}
      data={data}
      columns={filter(columns, (el) => [...internalIds, ...selectedColumns].includes(el.id as string))}
      GTColumnSelectAnchorExported={
        <GTColumnSelectAnchor
          tableId={TableIds.rmpScenarios}
          options={[
            { key: 'assessment_name', title: 'Assessment' },
            { key: 'ux_id', title: 'Id' },
            { key: 'status_scen', title: 'Status' },
            { key: 'name', title: 'Scenario' },
            { key: 'source', title: 'Source' },
            { key: 'event', title: 'Event' },
            { key: 'consequence', title: 'Consequence' },
            {
              key: 'risk_owner',
              title: 'Risk Owner',
            },
            {
              key: 'expected_loss',
              title: 'Expected Loss',
            },
            {
              key: 'risk_identified_date',
              title: 'Identified Date',
            },
            {
              key: 'updated_at',
              title: 'Last Updated',
            },
            {
              key: 'edited_at',
              title: 'Last Edited Date',
            },
            {
              key: 'created_at',
              title: 'Created Date',
            },
            {
              key: '_mod_applied',
              title: 'Modifiers',
            },
          ]}
          defaultOptions={defaultColumns}
          value={selectedColumns}
          onChange={setSelectedColumns}
        />
      }
      expandContent={(el) => {
        const scenario = el.scenario;
        if (!controlData || !isModStrategy || getIncompleteStateNew(scenario) === 'TECH') return null;
        return <RmpScenarioModifierExpand controlData={controlData} scenarioId={scenario.id} />;
      }}
      hideExpandContentWhenClosed
      darkMode
    />
  );
};
